import { ErrorHandler, Injectable } from '@angular/core'
import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { addBreadcrumb, setTag, setUser } from '@sentry/browser'
import { environment } from 'environments/environment'
import { OAuthCredential, OAuthProvider, signInWithCredential, signInWithPopup, UserCredential } from 'firebase/auth'
import { KirjauduTilintarkastajanaPyynto, KirjauduTilintarkastajanaVastaus, KirjauduV2Pyynto, KirjauduV2Vastaus, TarkistaSuperAdminVastaus } from '../../_jaettu-lemonator/model/kirjautuminen'
import { FirebaseLemonaid, FirebaseLemonator } from './firebase-lemonator.service'

export type AuthProviderName = 'microsoft'
export interface SsoLoginResult {
  rooliValinta: boolean
  linkitys?: { email: string | undefined; microsoftKirjautumisenVirheCredential: OAuthCredential }
}

@Injectable()
export class SsoLoginService {
  constructor(
    private _firebase: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _errorHandler: ErrorHandler,
  ) {}

  async kirjauduProviderilla(providerName: AuthProviderName): Promise<SsoLoginResult> {
    let vaihe = 'ms-popup'
    try {
      let provider: OAuthProvider
      if (providerName === 'microsoft') {
        provider = new OAuthProvider('microsoft.com')
        provider.setCustomParameters({
          prompt: 'select_account',
          tenant: environment.microsoftEntraTenantId,
        })
        provider.addScope('User.Read')
      }

      const result = await signInWithPopup(this._firebase.auth, provider)
      const credential = this._getCredentialFromProviderResult(result)

      if (result && result.user && credential) {
        setUser({ email: result.user.email || undefined })
        addBreadcrumb({ category: 'kirjautuminen', message: 'ms-popup ok' })

        vaihe = 'ms-credential'
        await signInWithCredential(this._firebase.auth, credential)
        addBreadcrumb({ category: 'kirjautuminen', message: 'ms-credential ok' })

        vaihe = 'tarkistaSuperAdmin'
        const { onSuperAdmin } = await this._firebase.functionsCall<null, TarkistaSuperAdminVastaus>('tarkistaSuperAdmin', null)
        addBreadcrumb({ category: 'kirjautuminen', message: 'tarkistaSuperAdmin ok' })
        if (onSuperAdmin) {
          return { rooliValinta: true }
        }
      }

      return { rooliValinta: false }
    } catch (error) {
      setTag('kirjautumisvaihe', vaihe)
      if (error.code !== 'auth/account-exists-with-different-credential') {
        this._errorHandler.handleError(error)
        throw error
      }

      // Käyttäjän tili on jo olemassa toisen (Google) providerin kanssa, palautetaan linkitys-data
      // minkä perusteella voi hoitaa tilien linkityksen
      const email = error.customData?.email as string | undefined

      // Happy case: oletetaan, että käsiteltävä virhe on tullut vain MS kirjautumisesta
      const microsoftKirjautumisenVirheCredential = OAuthProvider.credentialFromError(error)

      const provider: UserProvider = {
        uid: error.customData._tokenResponse.federatedId.replace('http://microsoft.com/', ''),
        providerId: error.customData._tokenResponse.providerId,
        email: error.customData._tokenResponse.email,
        displayName: error.customData._tokenResponse.displayName,
      }

      console.log('Provider to link:', provider)

      await this._firebase.functionsCall<UserProvider, {}>('linkitaMicrosoft', provider)

      // const result = await linkWithCredential(user, microsoftKirjautumisenVirheCredential)

      return {
        rooliValinta: false,
        linkitys: {
          email,
          microsoftKirjautumisenVirheCredential,
        },
      }
    }
  }

  async kirjauduCustom(rooli?: KirjanpitajanRooli, tilintarkastajaAvain?: string) {
    let vaihe = tilintarkastajaAvain ? 'kirjauduTilintarkastajana' : 'kirjauduV2'
    try {
      let kirjauduResult: KirjauduV2Vastaus

      if (tilintarkastajaAvain) {
        kirjauduResult = await this._firebase.functionsCall<KirjauduTilintarkastajanaPyynto, KirjauduTilintarkastajanaVastaus>('kirjauduTilintarkastajana', { tilintarkastajaAvain })
      } else {
        kirjauduResult = await this._firebase.functionsCall<KirjauduV2Pyynto, KirjauduV2Vastaus>('kirjauduV2', {
          role: rooli ? rooli.toString() : undefined,
        })
      }
      addBreadcrumb({ category: 'kirjautuminen', message: `${vaihe} ok` })

      vaihe = 'uloskirjautuminen'
      await Promise.all([this._firebase.authSignOut(), this._firebaseLemonaid.authSignOut()])
      addBreadcrumb({ category: 'kirjautuminen', message: 'uloskirjautuminen ok' })

      vaihe = 'custom-token-kirjautuminen'
      await Promise.all([this._firebase.authSignInWithCustomToken(kirjauduResult.lemonatorToken, false), this._firebaseLemonaid.authSignInWithCustomToken(kirjauduResult.lemonaidToken, false)])
      addBreadcrumb({ category: 'kirjautuminen', message: 'custom-token-kirjautuminen ok' })
    } catch (error) {
      setTag('kirjautumisvaihe', vaihe)
      throw error
    }
  }

  private _getCredentialFromProviderResult(result: UserCredential) {
    if (!result) {
      return null
    }
    return OAuthProvider.credentialFromResult(result)
  }
}

/**
 * Represents a user identity provider that can be associated with a Firebase user.
 */
export interface UserProvider {
  /**
   * The user identifier for the linked provider.
   */
  uid?: string
  /**
   * The display name for the linked provider.
   */
  displayName?: string
  /**
   * The email for the linked provider.
   */
  email?: string
  /**
   * The phone number for the linked provider.
   */
  phoneNumber?: string
  /**
   * The photo URL for the linked provider.
   */
  photoURL?: string
  /**
   * The linked provider ID (for example, "google.com" for the Google provider).
   */
  providerId?: string
}
